/**
 * @-Erwähnungen in Chat-Eingaben.
 *
 * Reine Logik, ohne React — damit sie testbar bleibt und Team-Chat wie
 * Kommunikations-Tab dieselbe Erkennung benutzen.
 *
 * Das Backend (App\Services\Notifications\MentionNotifier) erkennt in der
 * gesendeten Nachricht case-insensitiv und an Wortgrenzen:
 *   @Vorname Nachname · @Vorname · @Nachname · @email-localpart
 * Eingefügt wird deshalb immer „@Vorname Nachname" (bzw. der E-Mail-Teil,
 * wenn kein Name hinterlegt ist) — das trifft alle vier Formen.
 */
import type { V1Admin } from './v1';

/** Längster Suchbegriff hinter dem @ — darüber ist es keine Erwähnung mehr. */
const MAX_QUERY_LENGTH = 40;

export interface MentionQuery {
  /** Index des `@` im Text. */
  start: number;
  /** Text zwischen `@` und Cursor. */
  query: string;
}

/**
 * Findet die Erwähnung, in der der Cursor gerade steht.
 *
 * Ein `@` zählt nur am Wortanfang (Textanfang oder Whitespace davor), damit
 * E-Mail-Adressen im Fließtext keine Auswahlliste auslösen. Der Suchbegriff
 * darf ein Leerzeichen enthalten („@Anna Sch"), aber keinen Zeilenumbruch.
 */
export function findMentionQuery(text: string, caret: number): MentionQuery | null {
  if (caret < 0 || caret > text.length) return null;

  const upToCaret = text.slice(0, caret);
  const at = upToCaret.lastIndexOf('@');
  if (at === -1) return null;

  const charBefore = at > 0 ? upToCaret[at - 1] : '';
  if (charBefore !== '' && !/\s/.test(charBefore)) return null;

  const query = upToCaret.slice(at + 1);
  if (query.length > MAX_QUERY_LENGTH) return null;
  if (/[\n\r]/.test(query)) return null;
  // Ein Name hat höchstens zwei Wörter. Ab dem dritten ist der Satz
  // weitergegangen und die Liste hat nichts mehr zu suchen.
  if (query.trim().split(/\s+/).filter(Boolean).length > 2) return null;

  return { start: at, query };
}

/** Anzeigename eines Admins — dasselbe Format, das der Notifier erkennt. */
export function mentionName(a: V1Admin): string {
  const name = [a.vorname, a.nachname].filter(Boolean).join(' ').trim();
  if (name) return name;
  return (a.email ?? '').split('@')[0] ?? '';
}

/**
 * Admins, die zum Suchbegriff passen. Leerer Begriff → die ersten `limit`
 * Einträge, damit die Liste direkt nach dem `@` etwas anbietet.
 */
export function filterAdminsForMention(admins: V1Admin[], query: string, limit = 8): V1Admin[] {
  const q = query.trim().toLowerCase();
  const candidates = admins.filter(a => mentionName(a) !== '');

  if (q === '') return candidates.slice(0, limit);

  return candidates
    .filter(a => {
      const haystack = `${mentionName(a)} ${a.email ?? ''}`.toLowerCase();
      return haystack.includes(q);
    })
    .slice(0, limit);
}

export interface MentionInsertion {
  text: string;
  /** Neue Cursorposition (hinter dem eingefügten Leerzeichen). */
  caret: number;
}

/**
 * Ersetzt den angefangenen Suchbegriff durch die vollständige Erwähnung und
 * hängt ein Leerzeichen an, damit direkt weitergeschrieben werden kann.
 */
export function applyMention(text: string, mention: MentionQuery, admin: V1Admin, caret: number): MentionInsertion {
  const inserted = `@${mentionName(admin)} `;
  const next = text.slice(0, mention.start) + inserted + text.slice(caret);

  return { text: next, caret: mention.start + inserted.length };
}
